import React from 'react';
import { Element } from 'react-scroll';
import classNames from 'classnames';
import { CmsAsset, CmsPhase } from '../../integrations/contentful/types';
import { determineIconForMemoryPhase } from '../../utils/memories';
import { baseXUnit, breakpoints, color, spacing } from '../../styles/theme';
import H from '../elements/H';
import IconText from '../common/IconText';
import Spacing from '../common/Spacing';
import CmsTextareaContent from '../cms/CmsTextareaContent';
import ExpandableContent from '../common/ExpandableContent';
import CmsMediaCarousel from '../cms/CmsMediaCarousel';
import DisplayOn from '../common/DisplayOn';
import CmsMediaFixedHeight from '../cms/CmsMediaFixedHeight';
import Row from '../grid/Row';
import Col from '../grid/Col';
import DivButton from '../a11y/DivButton';
import { trackAnalyticsEvent } from '../../analytics/analytics';
import { MEMORY_PAGE_ANALYTICS_CATEGORY } from '../../pages/MemoryPage';
import MediaThumbnailList from './MediaThumbnailList';

interface MemoryPhaseProps {
  phase: CmsPhase;
  isFirstPhase: boolean;
  isLastPhase: boolean;
  onMediaClick?: (media: CmsAsset) => void;
  shipTrip: boolean;
}

// The offset of circles in the left "timeline" of the memory on desktop in pixels
const DESKTOP_DASHED_LINE_CIRCLE_TOP_OFFSET_PX = 172;
const MemoryPhase: React.FunctionComponent<MemoryPhaseProps> = ({
  phase,
  isFirstPhase,
  isLastPhase,
  onMediaClick,
  shipTrip
}) => {
  let mediaList = [];
  if (phase.fields.mainMedia) {
    mediaList.push(phase.fields.mainMedia);
  }
  if (phase.fields.media) {
    mediaList = mediaList.concat(phase.fields.media);
  }
  return (
    <div className="memory-phase">
      <Row>
        <Col size={[0, 0, 1]} className="first-dashed-line-col">
          <div
            className={classNames(
              'dashed-line',
              isFirstPhase ? 'first-dashed-line' : '',
              isLastPhase ? 'last-dashed-line' : ''
            )}
          />
          <div className="circle" />
        </Col>
        <Col size={[24, 24, 23]}>
          <Spacing top={[0, 0, 12]} bottom={[2, 2, 6]} left={[3, 3, 8]}>
            <DisplayOn small medium>
              <div className="circle" />
            </DisplayOn>
            <Element name={phase.sys.id}>
              <IconText
                key={phase.sys.id}
                icon={determineIconForMemoryPhase(
                  phase,
                  isFirstPhase,
                  shipTrip
                )}
                iconColor="gray"
              >
                <H styling="h4" tag="div" data>
                  {phase.fields.label}
                </H>
              </IconText>
              <Spacing top={[1, 1, 2]}>
                <H styling="h2" tag="h2">
                  <strong>{phase.fields.title}</strong>
                </H>
              </Spacing>
            </Element>
          </Spacing>
        </Col>
      </Row>
      <Row>
        <Col size={[0, 0, 1]}>
          {!isLastPhase && <div className="dashed-line" />}
        </Col>
        <Col size={[24, 24, 13]}>
          <Spacing bottom={3} left={[-2, -2, 0]} right={[-2, -2, 0]}>
            <DisplayOn small medium>
              <CmsMediaCarousel
                mediaList={mediaList}
                onMediaClick={onMediaClick}
              />
            </DisplayOn>
            <DisplayOn large>
              <div className="main-media-wrapper">
                <DivButton
                  onClick={() => {
                    const media = phase.fields.mainMedia;
                    media && onMediaClick && onMediaClick(media);
                  }}
                >
                  <CmsMediaFixedHeight
                    fixedHeight={[0, 0, 55]}
                    maxWidth={98}
                    media={mediaList[0]}
                    hover
                  />
                </DivButton>
              </div>
              {phase.fields.media && (
                <MediaThumbnailList
                  mediaList={phase.fields.media}
                  onMediaClick={onMediaClick}
                />
              )}
            </DisplayOn>
          </Spacing>
        </Col>
        <Col size={[0, 0, 1]}></Col>
        {!isLastPhase && (
          <div
            className={classNames(
              'dashed-line',
              'mobile-dashed-line',
              isFirstPhase ? 'first-dashed-line' : '',
              isLastPhase ? 'last-dashed-line' : ''
            )}
          />
        )}
        <Col size={[24, 24, 9]}>
          <div className="content">
            <DisplayOn large>
              {phase.fields.content && (
                <CmsTextareaContent content={phase.fields.content} />
              )}
            </DisplayOn>
            <DisplayOn small medium>
              <Spacing bottom={10}>
                <ExpandableContent
                  showExcerpt
                  onClick={() => {
                    trackAnalyticsEvent(
                      MEMORY_PAGE_ANALYTICS_CATEGORY,
                      'Avattu lomakokemuksen koko teksti',
                      phase.fields.title
                    );
                  }}
                >
                  {phase.fields.content && (
                    <CmsTextareaContent content={phase.fields.content} />
                  )}
                </ExpandableContent>
              </Spacing>
            </DisplayOn>
          </div>
        </Col>
      </Row>
      {/*language=CSS*/}
      <style jsx>{`
        .memory-phase {
          position: relative;
        }
        .main-media-wrapper {
          cursor: pointer;
          overflow: hidden;
          border-radius: ${baseXUnit(1)};
          position: relative;
        }

        .dashed-line {
          border-left: 2px dashed ${color.light};
          position: absolute;
          top: 10px;
          height: 100%;
          margin-left: -2px;
          z-index: -1;
        }

        .circle {
          position: absolute;
          margin-top: ${baseXUnit(1.4)};
          margin-left: -36px;
          height: 6px;
          width: 6px;
          border-radius: 50%;
          background-color: ${color.dark};
          z-index: 10;
        }

        .content {
          padding-left: ${spacing.two};
        }

        @media ${breakpoints.large} {
          .content {
            padding-left: 0;
          }
          .mobile-dashed-line {
            display: none;
          }
          .dashed-line {
            border: 0;
            width: 4px;
            background-image: url('/static/img/timeline-desktop.png');
            background-repeat: repeat-y;
            background-position: left;
            margin-left: 0;
            top: 0px;
          }
          :global(.first-dashed-line-col) {
            /*
            This is a way to prevent the dashed line of the first element bleeding over the one below it.
            Since the first dashed line does not begin from the top of the element, but instead lower, its bottom
            edge will overflow from its container on top of the next element -> ugly dashed line.
            */
            overflow-y: hidden;
          }
          .first-dashed-line {
            top: ${DESKTOP_DASHED_LINE_CIRCLE_TOP_OFFSET_PX}px;
          }
          .last-dashed-line {
            height: ${DESKTOP_DASHED_LINE_CIRCLE_TOP_OFFSET_PX}px;
          }
          .circle {
            position: absolute;
            margin-top: ${DESKTOP_DASHED_LINE_CIRCLE_TOP_OFFSET_PX}px;
            margin-left: -2px;
            height: 8px;
            width: 8px;
            border-radius: 50%;
            background-color: ${color.dark};
          }
          .first-circle {
            margin-top: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default MemoryPhase;
